import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import P2 from '@base/P2/P2';
import CalendarFilledBatch from '@icon/CalendarFilledBatch';
import { getDateMonthString, getDateShort } from '@utils/dates';
import { Spacings, mediaQuery, Fonts } from '@styles/index';
import H4 from '@base/H4/H4';
import { commasIndianFormat, currencyCode } from '@utils/textHelper';
import { getYear } from 'date-fns';
import { getEnrollmentEndMessage } from './utils';

const Flex = styled.div`
  display: flex;
  align-items: center;
  grid-column: span 5;
  margin-right: ${(props) =>
    props.$fromAboutDrawer ? '0' : Spacings.SPACING_10B};
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_4B};
    grid-column: span 10;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  height: ${Spacings.SPACING_12B};
  width: ${Spacings.SPACING_12B};
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  background-color: var(--color-i-yellow-12);
  ${mediaQuery.uptoMobile} {
    width: 48px;
    height: 48px;
  }
`;

const Info = styled.div`
  margin-left: ${({ $addMargin }) => ($addMargin ? Spacings.SPACING_4B : 0)};
  align-self: center;
  ${mediaQuery.uptoMobile} {
    margin-left: ${({ $addMargin }) => ($addMargin ? Spacings.SPACING_3B : 0)};
    & > h6 {
      font-size: ${Fonts.P1};
    }
  }
`;

const StyH4 = styled(H4)`
  margin-top: ${Spacings.SPACING_1B};
  display: ${({ $showBatchName }) =>
    $showBatchName ? 'inline-block' : 'unset'};
  width: ${({ $showBatchName }) => ($showBatchName ? '510px' : 'unset')};
  white-space: ${({ $showBatchName }) => ($showBatchName ? 'nowrap' : 'unset')};
  overflow: ${({ $showBatchName }) =>
    $showBatchName ? 'hidden !important' : 'unset'};
  text-overflow: ${({ $showBatchName }) =>
    $showBatchName ? 'ellipsis' : 'unset'};
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.P2};
    font-weight: ${Fonts.LIGHT};
  }
`;

const BatchEnrollmentDetails = ({
  startsAt,
  endsAt,
  enrollmentExpiry,
  showEnrollmentText,
  isRecordeBatch,
  activeStoreSku,
  isEnrolled,
  isHybrid,
  isStoreRoute,
  isSmallHeader,
  batchName,
  isDurationBased
}) => {
  const { price, maxRetailPrice, currency } = activeStoreSku || {};
  if (!startsAt && !(isHybrid || isRecordeBatch)) return null;
  const showBatchName = isSmallHeader && (isRecordeBatch || isHybrid);
  if (isRecordeBatch && !maxRetailPrice && !showBatchName) return null;
  const startsAtDate = new Date(startsAt);
  const endsAtDate = endsAt ? new Date(endsAt) : null;
  const completeEndDate = `${
    startsAtDate && getDateMonthString(startsAtDate, false, false)
  } ${startsAtDate && getYear(startsAtDate)}`;
  const startsOn = getDateShort(startsAtDate);
  const currentDate = new Date();
  let dateString = '';
  if (currentDate.getTime() < startsAtDate.getTime()) {
    dateString = `Starts on ${startsOn}`;
  } else if (currentDate.getTime() > startsAtDate.getTime() && !endsAtDate) {
    dateString = `Started on ${startsOn}`;
  } else if (
    currentDate.getTime() > startsAtDate.getTime() &&
    currentDate.getTime() < endsAtDate.getTime()
  ) {
    dateString = `Started on ${startsOn}`;
  } else {
    dateString = `Ended on ${getDateShort(endsAtDate)}`;
  }

  const getRecordedString = () => {
    if (!maxRetailPrice) return null;
    const infoText = isEnrolled
      ? 'You’ve joined this batch'
      : `Save upto ${currencyCode[currency]}${commasIndianFormat(
          maxRetailPrice - price
        )}`;
    return infoText;
  };

  const getSubString = () => {
    if ((isHybrid || isRecordeBatch) && isStoreRoute) {
      return enrollmentExpiry
        ? getEnrollmentEndMessage(enrollmentExpiry)
        : getRecordedString();
    }
    if (isDurationBased) {
      return completeEndDate;
    }
    return dateString;
  };

  const getRecordedBatchText = () => {
    if (showBatchName) return getSubString();
    return 'Start learning now';
  };

  const enrollmentText =
    isRecordeBatch || isHybrid
      ? getRecordedBatchText()
      : getEnrollmentEndMessage(enrollmentExpiry);

  const getEnrollmentText = () => {
    if (showEnrollmentText && enrollmentText) {
      return enrollmentText;
    }
    if (!enrollmentText && showBatchName) {
      return null;
    }
    if (isDurationBased) {
      return 'Batch starts from';
    }
    return 'Batch Schedule';
  };

  const showRedTitle =
    (showEnrollmentText && enrollmentText && !isRecordeBatch && !isHybrid) ||
    (isHybrid && showBatchName);

  const enrollmentSubstring = showBatchName ? batchName : getSubString();

  return (
    <Flex>
      {!isRecordeBatch && !isHybrid && (
        <IconWrapper>
          <CalendarFilledBatch
            size={Spacings.SPACING_22}
            color="var(--color-i-yellow-4)"
            viewBox="0 0 22 20"
          />
        </IconWrapper>
      )}
      {enrollmentSubstring && (
        <Info $addMargin={!isRecordeBatch && !isHybrid}>
          <P2
            color={
              showRedTitle
                ? 'var(--color-i-red)'
                : 'var(--color-text-secondary)'
            }
          >
            {getEnrollmentText()}
          </P2>
          <StyH4
            color="var(--color-text-primary)"
            $showBatchName={showBatchName}
          >
            {enrollmentSubstring}
          </StyH4>
        </Info>
      )}
    </Flex>
  );
};

export default BatchEnrollmentDetails;

BatchEnrollmentDetails.propTypes = {
  startsAt: PropTypes.string,
  endsAt: PropTypes.string
};

BatchEnrollmentDetails.defaultProps = {
  startsAt: null,
  endsAt: null
};
